import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { useAuth } from '@/hooks/useAuth'
import { setPersonalUser } from '@/lib/personal'
import { APPEARANCE_DEFAULTS, PLATFORM_THEME_DEFAULTS, applyTheme, computeTheme, resolveAppearance, type AppearancePrefs, type PlatformTheme } from '@/lib/themeEngine'
import { useOrgBranding } from '@/components/layout/BrandingApplier'

/**
 * Interface preferences (E-UX): theme, density, favourites, first-login welcome and "what's new".
 * Source of truth = the server (`/me/preferences`, so they follow the user across devices); a
 * localStorage mirror of theme/density lets index.html paint the right theme before React loads.
 * Every change is PATCHed and the UI re-renders from the server's read-back answer; on failure
 * the previous value is restored and the caller gets the error (no silent "saved").
 */
export type ThemePref = 'light' | 'dark' | 'system'
export type Density = 'comfortable' | 'compact'
export interface Favourite { path: string; title: string }
export interface Preferences extends AppearancePrefs {
  theme: ThemePref
  density: Density | null
  favourites: Favourite[]
  welcome_done: boolean
  whats_new_seen: string | null
}

const DEFAULTS: Preferences = { ...APPEARANCE_DEFAULTS, theme: 'system', density: null, favourites: [], welcome_done: false, whats_new_seen: null }
const KEY = ['me', 'preferences'] as const

export async function getPreferences(): Promise<Preferences> {
  return { ...DEFAULTS, ...((await api.get('/api/v1/me/preferences')).data as Partial<Preferences>) }
}
export async function patchPreferences(patch: Partial<Preferences>): Promise<Preferences> {
  return { ...DEFAULTS, ...((await api.patch('/api/v1/me/preferences', patch)).data as Partial<Preferences>) }
}

/** Platform-wide theme (public endpoint; also used before sign-in by the login page). */
export const PLATFORM_THEME_KEY = ['platform', 'theme'] as const
export async function getPlatformTheme(): Promise<PlatformTheme> {
  return { ...PLATFORM_THEME_DEFAULTS, ...((await api.get('/api/v1/platform/theme')).data as Partial<PlatformTheme>) }
}

/** Roles that live in registers get the compact density by default (spec 20 §3.11). */
const COMPACT_ROLES = ['HR_ADMIN', 'HR_EXECUTIVE', 'CA_PARTNER']

interface Ctx {
  prefs: Preferences
  /** The theme actually painted (system resolved). */
  resolvedTheme: 'light' | 'dark'
  density: Density
  /** Saves to the account; falls back to this device when the server cannot store it (never throws then). Throws only on a 422 refusal. */
  update: (patch: Partial<Preferences>) => Promise<SaveResult>
  saving: boolean
}
const PrefsContext = createContext<Ctx | null>(null)

function systemDark() {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches
}

function mirror(prefs: Preferences, density: Density) {
  try {
    localStorage.setItem('bigroi.theme', prefs.theme)
    localStorage.setItem('bigroi.density', density)
  } catch { /* storage unavailable */ }
}

/**
 * Preferences are cosmetic: a failed save must never block the user, re-show the welcome tour on
 * every load, or raise a red error (owner-reported, 2026-10-01: a server with a pending migration
 * answered every save with an error). When the server cannot store a change it is kept on this
 * device (`bigroi.prefs.local.<user>`), layered over the server values, and synced once the
 * server accepts it. Only a genuine validation refusal (422, e.g. a 21st favourite) is an error.
 */
const LOCAL_PREFIX = 'bigroi.prefs.local.'
function readLocal(uid: string | undefined): Partial<Preferences> {
  if (!uid) return {}
  try { const v = JSON.parse(localStorage.getItem(LOCAL_PREFIX + uid) ?? '{}'); return v && typeof v === 'object' ? v : {} } catch { return {} }
}
function writeLocal(uid: string, v: Partial<Preferences>) {
  try { if (Object.keys(v).length) localStorage.setItem(LOCAL_PREFIX + uid, JSON.stringify(v)); else localStorage.removeItem(LOCAL_PREFIX + uid) } catch { /* storage unavailable */ }
}
export type SaveResult = { where: 'server' | 'device'; prefs: Preferences }

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const uid = user?.id
  const seeded = (user as unknown as { ui_preferences?: Partial<Preferences> | null } | null)?.ui_preferences
  const q = useQuery({
    queryKey: KEY,
    queryFn: getPreferences,
    enabled: !!user,
    staleTime: Infinity,
    initialData: user ? { ...DEFAULTS, ...(seeded ?? {}) } : undefined,
    retry: false,
    meta: { inlineError: true },
  })
  const [local, setLocal] = useState<Partial<Preferences>>(() => readLocal(uid))
  useEffect(() => { setLocal(readLocal(uid)) }, [uid])
  const server: Preferences = q.data ?? DEFAULTS
  const prefs: Preferences = user ? { ...server, ...local } : { ...DEFAULTS, theme: readLocalTheme() }
  const [sysDark, setSysDark] = useState(systemDark)
  const [saving, setSaving] = useState(false)

  useEffect(() => { setPersonalUser(user?.id ?? null) }, [user?.id])

  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)')
    if (!mq) return
    const on = () => setSysDark(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])

  // Platform default -> organisation branding (if allowed) -> this user's choice (if allowed).
  const platformQ = useQuery({ queryKey: PLATFORM_THEME_KEY, queryFn: getPlatformTheme, staleTime: 5 * 60_000, retry: false, meta: { inlineError: true } })
  const platform = platformQ.data ?? PLATFORM_THEME_DEFAULTS
  const orgQ = useOrgBranding()
  const eff = resolveAppearance(platform, orgQ.data?.accent_color ?? null, user ? prefs : null)
  const resolvedTheme: 'light' | 'dark' = eff.mode === 'system' ? (sysDark ? 'dark' : 'light') : eff.mode
  const density: Density = eff.density ?? (user && COMPACT_ROLES.includes(user.role) ? 'compact' : 'comfortable')

  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-theme', resolvedTheme)
    root.setAttribute('data-density', density)
    root.setAttribute('data-login-style', platform.login_style)
    if (user) mirror(prefs, density)
  }, [resolvedTheme, density, prefs, user, platform.login_style])

  // Accent / fonts / corner radius / sidebar style: applied to :root and cached for first paint.
  const { accent, font, heading_font, radius, sidebar } = eff
  useEffect(() => {
    applyTheme(computeTheme({ accent, font, heading_font, radius, sidebar }), resolvedTheme, sidebar)
  }, [accent, font, heading_font, radius, sidebar, resolvedTheme])

  // Device-only changes from an earlier failed save: try once to sync them to the account.
  const synced = useRef<string | null>(null)
  useEffect(() => {
    if (!uid || synced.current === uid || !q.isSuccess) return
    synced.current = uid
    const pending = readLocal(uid)
    if (!Object.keys(pending).length) return
    patchPreferences(pending).then((saved) => {
      qc.setQueryData(KEY, saved); writeLocal(uid, {}); setLocal({})
    }).catch(() => { /* still not possible: stays on this device */ })
  }, [uid, q.isSuccess, qc])

  const update = useCallback(async (patch: Partial<Preferences>): Promise<SaveResult> => {
    if (!uid) throw new Error('Not signed in')
    const prev = qc.getQueryData<Preferences>(KEY) ?? DEFAULTS
    const prevLocal = readLocal(uid)
    // Show the change at once.
    qc.setQueryData<Preferences>(KEY, { ...prev, ...patch })
    setSaving(true)
    try {
      const saved = await patchPreferences(patch)
      qc.setQueryData(KEY, saved)
      const rest = { ...prevLocal }
      Object.keys(patch).forEach((k) => { delete (rest as Record<string, unknown>)[k] })
      writeLocal(uid, rest); setLocal(rest)
      return { where: 'server', prefs: { ...saved, ...rest } }
    } catch (e) {
      const status = (e as { status?: number }).status
      if (status === 422) {
        qc.setQueryData(KEY, prev)
        throw e
      }
      // Server could not store it (outage, pending migration…): keep it on this device.
      console.warn('[preferences] saved on this device only; the server did not accept the change', { status, keys: Object.keys(patch) })
      const next = { ...prevLocal, ...patch }
      writeLocal(uid, next); setLocal(next)
      qc.setQueryData(KEY, prev)
      return { where: 'device', prefs: { ...prev, ...next } }
    } finally {
      setSaving(false)
    }
  }, [uid, qc])

  const value = useMemo<Ctx>(() => ({ prefs, resolvedTheme, density, update, saving }), [prefs, resolvedTheme, density, update, saving])
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>
}

function readLocalTheme(): ThemePref {
  try {
    const t = localStorage.getItem('bigroi.theme')
    return t === 'dark' || t === 'light' ? t : 'system'
  } catch { return 'system' }
}

export function usePreferences(): Ctx {
  const ctx = useContext(PrefsContext)
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>')
  return ctx
}
